import React, { useEffect, useMemo } from "react";

import { SelectInput } from "~/core";
import { messages as globalMessages } from "~/i18n-messages";
import {
    IS_SELECTABLE,
    useLoggedInUserProductScopeOrgLevelGuid,
} from "~/utils/products/use-product-list";

import { FormatCropTooltipMessage } from "~/utils/crops/use-crop-list";
import {
    ICropPurposeDropdownOption,
    isCropPurposeUnlinked,
    toCropPurposeDropdownOptions,
    toCropPurposeSelectOptions,
    useCropPurposeList,
} from "~/utils/crops/use-crop-purpose-list";

export interface ICropPurposeSelectProps {
    cropGuid: string;
    /** Scopes the purpose list to a customer instead of the logged-in user's product-scope org
     * level -- for callers (e.g. Field Info) whose screen is already customer-scoped. Takes
     * precedence over the org-level scope when given, same as `useCropPurposeList` itself. */
    customerGuid?: string | null;
    /** "" (default) for callers that always store a guid field as a plain string; null for callers
     * (e.g. Attribute Color Override) that distinguish a genuine null clear from an empty string. */
    emptyValue?: "" | null;
    /** "containerClassNames" (default) applies the "select-form-input-error" class to the outer
     * container, matching most Purpose pickers; "hasError" instead passes SelectInput's own
     * hasError prop through to its inner TextInput -- a different, not-interchangeable mechanism. */
    errorStyle?: "containerClassNames" | "hasError";
    formatMessage: FormatCropTooltipMessage;
    onChange: (cropPurposeGuid: string | null, cropPurposeName: string | null) => void;
    onOptionsChange?: (options: ICropPurposeDropdownOption[]) => void;
    openOnFocus?: boolean;
    placeholderText: string;
    tabIndex?: number;
    testId: string;
    value: string;
}

/**
 * Hosts `useCropPurposeList()` for a Purpose picker whose host is a class component (the hook
 * can't be called there directly). Scoped to whichever crop is currently selected on the row being
 * edited, mirroring how Crop itself is restricted in the same host. Reports its stamped options
 * back up via `onOptionsChange`, when given, so a class's Save gate can read linkage state it has
 * no other way to reach.
 *
 * Shared by `crop-info-list.jsx`, `equation-list/crop-list.jsx`, `attribute-color-override.tsx`,
 * `base-yield-list.tsx`, and `field-information-detail.tsx` -- previously near-identical copies of
 * this same component. The `emptyValue`/`errorStyle`/`openOnFocus`/`tabIndex`/`customerGuid` props
 * exist because those sites weren't actually behaviorally identical; each knob maps to one
 * confirmed, real difference between them.
 */
export const CropPurposeSelect = ({
    cropGuid,
    customerGuid,
    emptyValue = "",
    errorStyle = "containerClassNames",
    formatMessage,
    onChange,
    onOptionsChange,
    openOnFocus,
    placeholderText,
    tabIndex,
    testId,
    value,
}: ICropPurposeSelectProps): JSX.Element => {
    const orgLevelGuid = useLoggedInUserProductScopeOrgLevelGuid();
    const { cropPurposes } = useCropPurposeList({ cropGuid, customerGuid, orgLevelGuid });
    const dropdownOptions = useMemo(
        () => toCropPurposeDropdownOptions(cropPurposes),
        [cropPurposes]
    );
    const selectOptions = useMemo(
        () => toCropPurposeSelectOptions(dropdownOptions, formatMessage),
        [dropdownOptions, formatMessage]
    );
    const isSelectedCropPurposeUnlinked = isCropPurposeUnlinked(value, dropdownOptions);

    useEffect(() => {
        onOptionsChange?.(dropdownOptions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownOptions]);

    return (
        <SelectInput
            tabIndex={tabIndex}
            openOnFocus={openOnFocus}
            optionIsHiddenKey={IS_SELECTABLE}
            options={selectOptions}
            placeholderText={placeholderText}
            value={value || ""}
            {...(errorStyle === "hasError"
                ? { hasError: isSelectedCropPurposeUnlinked }
                : {
                      containerClassNames: [
                          { "select-form-input-error": isSelectedCropPurposeUnlinked },
                      ],
                  })}
            toolTip={
                isSelectedCropPurposeUnlinked
                    ? formatMessage(globalMessages.cropPurposeNotLinkedInAgvance)
                    : undefined
            }
            onChange={(newValue) => {
                const selected = selectOptions.find((option) => option.value === newValue);
                onChange(newValue || emptyValue, selected ? selected.label : emptyValue);
            }}
            testId={testId}
        />
    );
};
